import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedClient } from './queries'
import type { components, operations } from './schema'

export type RunResults = components['schemas']['RunResultsRead']
export type RunExample = components['schemas']['RunExampleRead']
export type Plan = components['schemas']['PlanRead']
type ListQuery = NonNullable<
  operations['list_runs_api_v1_setup_runs_get']['parameters']['query']
>
type ExamplesQuery = NonNullable<
  operations['get_examples_api_v1_setup_runs__run_id__examples_get']['parameters']['query']
>

/** The examples key for emails that got no label. */
export const NO_LABEL = '__none__'

const keys = {
  run: (id: string) => ['setup-runs', id] as const,
  examples: (id: string, label: string, inbox?: string) =>
    ['setup-runs', id, 'examples', label, inbox ?? null] as const,
  list: (inbox?: string) => ['setup-runs', 'list', inbox ?? null] as const,
}

export function isBusy(run: RunResults | null | undefined): boolean {
  return (
    run?.status === 'running' ||
    run?.undo.status === 'queued' ||
    run?.undo.status === 'running'
  )
}

export function useSetupRun(runId: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: keys.run(runId),
    queryFn: async () => {
      const client = await getClient()
      const { data, error, response } = await client.GET(
        '/api/v1/setup-runs/{run_id}',
        { params: { path: { run_id: runId } }, cache: 'no-store' },
      )
      // No such run: the approval never became a run (or isn't a past-mail run).
      if (response.status === 404) return null
      if (error || !data) throw new Error('Failed to load the run')
      return data
    },
    refetchInterval: (query) => (isBusy(query.state.data) ? 3000 : false),
  })
}

export function useRunExamples(
  runId: string,
  label: string | null,
  inboxId?: string,
) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: keys.examples(runId, label ?? '', inboxId),
    enabled: label !== null,
    queryFn: async () => {
      const client = await getClient()
      const query = {
        label: label ?? NO_LABEL,
        inbox_id: inboxId,
      } satisfies ExamplesQuery
      const { data, error } = await client.GET(
        '/api/v1/setup-runs/{run_id}/examples',
        { params: { path: { run_id: runId }, query }, cache: 'no-store' },
      )
      if (error || !data) throw new Error('Failed to load examples')
      return data
    },
  })
}

export function useSetupRuns(inboxId?: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: keys.list(inboxId),
    queryFn: async () => {
      const client = await getClient()
      const query = { inbox_id: inboxId } satisfies ListQuery
      const { data, error } = await client.GET('/api/v1/setup-runs', {
        params: { query },
        cache: 'no-store',
      })
      if (error || !data) throw new Error('Failed to load runs')
      return data
    },
  })
}

function useRunAction(
  runId: string,
  action: 'stop' | 'undo' | 'sort-new-mail',
) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: async (deleteCreated?: boolean) => {
      const client = await getClient()
      const path = { params: { path: { run_id: runId } } }
      const { data, error } =
        action === 'undo'
          ? await client.POST('/api/v1/setup-runs/{run_id}/undo', {
              ...path,
              body: { delete_created: deleteCreated ?? false },
            })
          : action === 'stop'
            ? await client.POST('/api/v1/setup-runs/{run_id}/stop', path)
            : await client.POST(
                '/api/v1/setup-runs/{run_id}/sort-new-mail',
                path,
              )
      if (error || !data) throw new Error('The request failed')
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(keys.run(runId), data),
  })
}

export const useStopRun = (runId: string) => useRunAction(runId, 'stop')
export const useUndoRun = (runId: string) => useRunAction(runId, 'undo')
export const useSortNewMail = (runId: string) =>
  useRunAction(runId, 'sort-new-mail')
